/**
 * RiskManagementPage.js - Risco, Fraude e Disciplina
 * BLOCO 4: Controlo de Risco e Compliance
 * 
 * Funcionalidades:
 * - Controlo de Risco de Clientes
 * - Gestão de Fraudes Internas
 * - Análise por Loja
 */

import { useState, useEffect, useMemo, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { 
  Shield, AlertTriangle, Users, Store, TrendingUp, TrendingDown,
  Lock, Unlock, DollarSign, Eye, UserX, AlertCircle, CheckCircle,
  FileText, History, Download, Package, Wrench, Wallet, SearchCheck, Building2, ClipboardList, Siren, Scale, ShieldAlert
} from 'lucide-react';
import { formatCurrency, formatDateTime, formatDisplayCode, cn } from '@/lib/utils';
import GlobalFilters from '@/components/GlobalFilters';
import { DASHBOARD_SURFACE_CLASSNAME, DashboardStatCard } from '@/components/common/DashboardStatCard';
import { useGlobalFilters } from '@/hooks/useGlobalFilters';
import { useAuth } from '@/hooks/useAuth';
import { getStoredAuthToken } from '@/lib/authStorage';
import { COMPACT_ACTION_BUTTONS } from '@/lib/tableDensity';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: API_BASE_URL });
api.interceptors.request.use(config => {
  const token = getStoredAuthToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const riskAPI = {
  // Client Risk
  getClientRiskAssessment: () => api.get('/api/risk-management/clients/risk-assessment'),
  setClientCreditLimit: (data) => api.post('/api/risk-management/clients/credit-limit', data),
  getClientPaymentHistory: (id) => api.get(`/api/risk-management/clients/${id}/payment-history`),
  
  // Fraud
  getFraudAlerts: (params) => api.get('/api/risk-management/fraud/alerts', { params }),
  getSuspiciousActivities: (params) => api.get('/api/risk-management/fraud/suspicious-activities', { params }),
  investigateFraud: (id, data) => api.put(`/api/risk-management/fraud/${id}/investigate`, data),
  getAuditOperations: (params) => api.get('/api/risk-management/audit/operations', { params }),
  exportAuditOperationsPdf: (params) => api.get('/api/risk-management/audit/operations/pdf', { params, responseType: 'blob' }),
  
  // Location Performance
  getLocationPerformance: (params) => api.get('/api/risk-management/locations/performance', { params }),
  getLocationHealth: (id) => api.get(`/api/risk-management/locations/${id}/health`),
  exportLocationPerformancePdf: (params) => api.get('/api/risk-management/locations/performance/pdf', { params, responseType: 'blob' }),
  exportLocationPerformanceXlsx: (params) => api.get('/api/risk-management/locations/performance/xlsx', { params, responseType: 'blob' })
};

const RISK_PANEL = DASHBOARD_SURFACE_CLASSNAME;

const riskLevelColors = {
  low: 'bg-green-100 text-green-800 border-green-300',
  medium: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  high: 'bg-orange-100 text-orange-800 border-orange-300',
  blocked: 'bg-red-100 text-red-800 border-red-300'
};

const riskLevelLabels = {
  low: 'Baixo',
  medium: 'Médio',
  high: 'Alto',
  blocked: 'Bloqueado'
};

const LOCATION_TYPE_META = {
  store: {
    label: 'store',
    badgeClass: 'bg-sky-100 text-sky-700 border-sky-200',
    iconWrap: 'bg-sky-100 text-sky-700',
    icon: Store,
  },
  warehouse: {
    label: 'warehouse',
    badgeClass: 'bg-violet-100 text-violet-700 border-violet-200',
    iconWrap: 'bg-violet-100 text-violet-700',
    icon: Package,
  },
  repair: {
    label: 'repair',
    badgeClass: 'bg-orange-100 text-orange-700 border-orange-200',
    iconWrap: 'bg-orange-100 text-orange-700',
    icon: Wrench,
  },
  default: {
    label: 'location',
    badgeClass: 'bg-slate-100 text-slate-700 border-slate-200',
    iconWrap: 'bg-slate-100 text-slate-700',
    icon: Store,
  },
};

const RISK_ACCENTS = {
  blue: {
    icon: 'from-blue-500 to-indigo-600',
    header: 'from-blue-50 via-white to-indigo-50',
  },
  emerald: {
    icon: 'from-emerald-500 to-teal-600',
    header: 'from-emerald-50 via-white to-teal-50',
  },
  orange: {
    icon: 'from-orange-500 to-amber-600',
    header: 'from-orange-50 via-white to-amber-50',
  },
  purple: {
    icon: 'from-purple-500 to-violet-600',
    header: 'from-violet-50 via-white to-purple-50',
  },
  rose: {
    icon: 'from-rose-500 to-red-600',
    header: 'from-rose-50 via-white to-red-50',
  },
  cyan: {
    icon: 'from-cyan-500 to-blue-600',
    header: 'from-cyan-50 via-white to-blue-50',
  },
  slate: {
    icon: 'from-slate-700 to-slate-900',
    header: 'from-slate-50 via-white to-slate-100',
  },
};

const getRiskAccent = (accent) => RISK_ACCENTS[accent] || RISK_ACCENTS.blue;

const RiskSectionCard = ({ title, description, icon: Icon, accent = 'blue', badge, testId, children, action }) => {
  const accentMeta = getRiskAccent(accent);

  return (
    <Card className={cn(RISK_PANEL, 'overflow-hidden')} data-testid={testId}>
      <CardHeader className={cn('border-b border-slate-200/70 bg-gradient-to-br p-4 sm:p-5', accentMeta.header)}>
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-3">
              <div className={cn('rounded-2xl bg-gradient-to-br p-3.5 text-white shadow-lg', accentMeta.icon)}>
                <Icon className="h-5 w-5" />
              </div>
              {badge ? <Badge variant="outline" className="rounded-full border-slate-200 bg-white/80 px-3 py-1 text-[10px] uppercase tracking-[0.14em] text-slate-600">{badge}</Badge> : null}
            </div>
            <div>
              <CardTitle className="text-lg font-semibold tracking-tight text-slate-900">{title}</CardTitle>
              {description ? <CardDescription className="mt-1 max-w-3xl text-xs leading-relaxed text-slate-600">{description}</CardDescription> : null}
            </div>
          </div>
          {action ? <div className="flex flex-wrap items-center gap-2">{action}</div> : null}
        </div>
      </CardHeader>
      <CardContent className="space-y-4 p-4 sm:p-5">{children}</CardContent>
    </Card>
  );
};

const RiskMetricTile = ({ label, value, helper, icon: Icon, accent = 'blue', testId, valueClassName, footer = null }) => {
  const accentMeta = getRiskAccent(accent);

  return (
    <div className="rounded-2xl border border-slate-200/80 bg-white/90 p-4 shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md" data-testid={testId}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[11px] uppercase tracking-[0.16em] text-slate-500">{label}</p>
          {helper ? <p className="mt-1 text-xs leading-relaxed text-slate-500">{helper}</p> : null}
        </div>
        <div className={cn('rounded-2xl bg-gradient-to-br p-2.5 text-white shadow-md', accentMeta.icon)}>
          <Icon className="h-4 w-4" />
        </div>
      </div>
      <p className={cn('mt-4 text-xl font-semibold text-slate-950', valueClassName)}>{value}</p>
      {footer ? <div className="mt-3">{footer}</div> : null}
    </div>
  );
};

const RiskListRow = ({ title, subtitle, value, icon: Icon, accent = 'blue', badges = [], testId, valueHelper, valueClassName, action = null, leading = null, rowClassName = '' }) => {
  const accentMeta = getRiskAccent(accent);

  return (
    <div className={cn('flex flex-col gap-3 rounded-2xl border border-slate-200/80 bg-white/90 p-4 shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md lg:flex-row lg:items-center lg:justify-between', rowClassName)} data-testid={testId}>
      <div className="flex min-w-0 items-start gap-3">
        {leading || (
          <div className={cn('rounded-2xl bg-gradient-to-br p-2.5 text-white shadow-md', accentMeta.icon)}>
            <Icon className="h-4 w-4" />
          </div>
        )}
        <div className="min-w-0">
          <p className="text-sm font-semibold text-slate-900">{title}</p>
          {subtitle ? <p className="mt-1 text-xs leading-relaxed text-slate-500">{subtitle}</p> : null}
          {badges.length ? <div className="mt-2 flex flex-wrap gap-1.5">{badges}</div> : null}
        </div>
      </div>
      <div className="flex flex-col gap-2 lg:items-end">
        <div className="lg:text-right">
          <p className={cn('text-base font-semibold text-slate-900', valueClassName)}>{value}</p>
          {valueHelper ? <p className="mt-1 text-xs text-slate-500">{valueHelper}</p> : null}
        </div>
        {action}
      </div>
    </div>
  );
};

export default function RiskManagementPage() {
  const [activeTab, setActiveTab] = useState('clients');
  const [loading, setLoading] = useState(true);
  
  // Data states
  const [clientRisk, setClientRisk] = useState(null);
  const [suspiciousActivities, setSuspiciousActivities] = useState(null);
  const [fraudAlerts, setFraudAlerts] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [locationPerformance, setLocationPerformance] = useState(null);
  const loadDataRef = useRef(null);
  const [exportingReport, setExportingReport] = useState('');
  
  // Dialogs
  const [creditLimitDialogOpen, setCreditLimitDialogOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [creditLimit, setCreditLimit] = useState('');
  const [selectedLocationHealth, setSelectedLocationHealth] = useState(null);
  const [locationDetailDialogOpen, setLocationDetailDialogOpen] = useState(false);
  const [loadingLocationDetailId, setLoadingLocationDetailId] = useState('');
  
  const { filters, setFilters } = useGlobalFilters({
    period: 'all',
    location_id: 'all'
  });
  
  const { user, hasRole } = useAuth();
  const isAdmin = hasRole(['admin']);

  useEffect(() => {
    loadDataRef.current?.();
  }, [filters]);

  const loadData = async () => {
    try {
      setLoading(true);
      const params = { period: filters.period };
      if (filters.location_id && filters.location_id !== 'all') {
        params.location_id = filters.location_id;
      }
      
      const [clientRes, suspiciousRes, fraudRes, auditRes, perfRes] = await Promise.all([
        riskAPI.getClientRiskAssessment(),
        riskAPI.getSuspiciousActivities(params),
        riskAPI.getFraudAlerts({ status: 'pending', period: filters.period }),
        riskAPI.getAuditOperations({ period: filters.period }),
        riskAPI.getLocationPerformance(params)
      ]);
      
      setClientRisk(clientRes.data);
      setSuspiciousActivities(suspiciousRes.data);
      setFraudAlerts(fraudRes.data || []);
      setAuditLogs(auditRes.data || []);
      setLocationPerformance(perfRes.data);
    } catch (error) {
      console.error('Failed to load risk data:', error);
      toast.error('Erro ao carregar dados de risco');
    } finally {
      setLoading(false);
    }
  };

  loadDataRef.current = loadData;

  const riskSummaryCards = [
    {
      title: 'Dívida Total',
      value: clientRisk?.summary?.total_outstanding_debt || 0,
      icon: DollarSign,
      color: 'blue',
      emphasis: 'primary',
      subtitle: 'exposição total do período',
      testId: 'risk-summary-total-debt-card',
    },
    {
      title: 'Actividades Suspeitas',
      value: suspiciousActivities?.total_suspicious || 0,
      format: 'number',
      icon: AlertTriangle,
      color: 'orange',
      emphasis: 'primary',
      subtitle: 'sinais operacionais activos',
      testId: 'risk-summary-suspicious-activities-card',
    },
    {
      title: 'Clientes Alto Risco',
      value: clientRisk?.summary?.high_risk || 0,
      format: 'number',
      icon: Users,
      color: 'red',
      subtitle: 'prioridade de monitoria',
      testId: 'risk-summary-high-risk-clients-card',
    },
    {
      title: 'Clientes Bloqueados',
      value: clientRisk?.summary?.blocked || 0,
      format: 'number',
      icon: Lock,
      color: 'orange',
      subtitle: 'restrições activas',
      testId: 'risk-summary-blocked-clients-card',
    },
    {
      title: 'Lojas Deficitárias',
      value: (locationPerformance?.warnings?.draining_locations || []).length,
      format: 'number',
      icon: Store,
      color: 'purple',
      subtitle: 'unidades a exigir acção',
      testId: 'risk-summary-draining-locations-card',
    },
    {
      title: 'Alertas de Fraude',
      value: fraudAlerts.length || 0,
      format: 'number',
      icon: Shield,
      color: 'cyan',
      subtitle: 'casos pendentes',
      testId: 'risk-summary-fraud-alerts-card',
    },
  ];

  const handleSetCreditLimit = async () => {
    if (!selectedClient || !creditLimit) return;
    
    try {
      await riskAPI.setClientCreditLimit({
        client_id: selectedClient.client_id,
        credit_limit: parseFloat(creditLimit)
      });
      toast.success('Limite de crédito definido');
      setCreditLimitDialogOpen(false);
      setSelectedClient(null);
      setCreditLimit('');
      loadData();
    } catch (error) {
      toast.error('Erro ao definir limite');
    }
  };

  const downloadPdfReport = async (reportKey, request, filename) => {
    try {
      setExportingReport(reportKey);
      const response = await request();
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      window.URL.revokeObjectURL(url);
      anchor.remove();
      toast.success('PDF premium exportado com sucesso');
    } catch (error) {
      console.error('Risk PDF export error:', error);
      toast.error('Erro ao exportar PDF premium');
    } finally {
      setExportingReport('');
    }
  };

  const downloadSpreadsheetReport = async (reportKey, request, filename) => {
    try {
      setExportingReport(reportKey);
      const response = await request();
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      window.URL.revokeObjectURL(url);
      anchor.remove();
      toast.success('Excel exportado com sucesso');
    } catch (error) {
      console.error('Risk XLSX export error:', error);
      toast.error('Erro ao exportar Excel');
    } finally {
      setExportingReport('');
    }
  };

  const handleViewLocationDetails = async (locationId) => {
    try {
      setLoadingLocationDetailId(locationId);
      const response = await riskAPI.getLocationHealth(locationId);
      setSelectedLocationHealth(response.data);
      setLocationDetailDialogOpen(true);
    } catch (error) {
      console.error('Failed to load location health:', error);
      toast.error('Erro ao carregar detalhes da loja');
    } finally {
      setLoadingLocationDetailId('');
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex justify-center items-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6" data-testid="risk-management-page">
      {/* Header */}
      <div className={cn(RISK_PANEL, 'flex flex-col gap-4 p-6 lg:flex-row lg:items-end lg:justify-between')}>
        <div className="space-y-2">
          <h1 className="flex items-center gap-2 text-3xl font-semibold tracking-tight" style={{fontFamily: 'Manrope'}}>
            <Shield className="h-8 w-8 text-red-600" />
            Gestão de Risco
          </h1>
          <p className="text-sm text-muted-foreground">
            Controlo de clientes, fraude e disciplina financeira
          </p>
          <div className="flex flex-wrap gap-2 text-xs text-slate-600">
            <Badge variant="outline" className="rounded-full px-3 py-1">Período: {filters.period}</Badge>
            <Badge variant="outline" className="rounded-full px-3 py-1">Utilizador: {user?.full_name || user?.email || 'Sistema'}</Badge>
          </div>
        </div>
      </div>

      {/* Global Filters */}
      <GlobalFilters 
        filters={['period', 'location']}
        onChange={(newFilters) => setFilters(newFilters)}
        defaultPeriod={filters.period}
      />

      {/* Summary KPIs */}
      <div className="grid gap-4 xl:grid-cols-12" data-testid="risk-summary-cards-grid">
        <div className="xl:col-span-6">
          <DashboardStatCard {...riskSummaryCards[0]} />
        </div>
        <div className="xl:col-span-6">
          <DashboardStatCard {...riskSummaryCards[1]} />
        </div>
        <div className="grid gap-4 md:grid-cols-2 xl:col-span-12 xl:grid-cols-4">
          {riskSummaryCards.slice(2).map((card) => (
            <DashboardStatCard key={card.testId} {...card} />
          ))}
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full grid-cols-2 rounded-2xl border border-slate-200 bg-slate-100/90 p-1 lg:grid-cols-4" data-testid="risk-tabs-list">
          <TabsTrigger value="clients" className="h-10 rounded-xl text-xs font-medium" data-testid="risk-tab-clients">Risco Clientes</TabsTrigger>
          <TabsTrigger value="fraud" className="h-10 rounded-xl text-xs font-medium" data-testid="risk-tab-fraud">Fraude</TabsTrigger>
          <TabsTrigger value="locations" className="h-10 rounded-xl text-xs font-medium" data-testid="risk-tab-locations">Lojas</TabsTrigger>
          <TabsTrigger value="audit" className="h-10 rounded-xl text-xs font-medium" data-testid="risk-tab-audit">Auditoria</TabsTrigger>
        </TabsList>

        {/* Clients Tab */}
        <TabsContent value="clients" className="mt-0 space-y-4">
          <RiskSectionCard
            title="Avaliação de Risco de Clientes"
            description={`${clientRisk?.summary?.total_clients || 0} clientes monitorados · Dívida total ${formatCurrency(clientRisk?.summary?.total_outstanding_debt || 0)}`}
            icon={Users}
            accent="blue"
            badge="Clientes"
            testId="risk-clients-card"
          >
              <div className="grid gap-4 md:grid-cols-3">
                <RiskMetricTile label="Clientes alto risco" value={clientRisk?.summary?.high_risk || 0} helper="Prioridade imediata de revisão" icon={ShieldAlert} accent="rose" testId="risk-clients-high-risk-metric" valueClassName="text-rose-700" />
                <RiskMetricTile label="Clientes bloqueados" value={clientRisk?.summary?.blocked || 0} helper="Sem aprovação automática de crédito" icon={Lock} accent="orange" testId="risk-clients-blocked-metric" valueClassName="text-orange-700" />
                <RiskMetricTile label="Exposição total" value={formatCurrency(clientRisk?.summary?.total_outstanding_debt || 0)} helper="Montante em dívida actualmente aberto" icon={Wallet} accent="purple" testId="risk-clients-total-debt-metric" />
              </div>

              {(clientRisk?.clients || []).filter(c => c.current_debt > 0 || c.risk_level !== 'low').length === 0 ? (
                <p className="text-center text-muted-foreground py-8">Nenhum cliente com risco</p>
              ) : (
                <div className="space-y-3">
                  {(clientRisk?.clients || [])
                    .filter(c => c.current_debt > 0 || c.risk_level !== 'low')
                    .slice(0, 20)
                    .map(client => (
                    <RiskListRow
                      key={client.client_id}
                      title={client.client_name}
                      subtitle={`${client.total_sales} compras · ${client.late_payments} atrasos`}
                      value={formatCurrency(client.current_debt)}
                      icon={client.should_block ? Lock : Users}
                      accent={client.should_block ? 'rose' : client.risk_level === 'high' ? 'orange' : 'blue'}
                      testId={`risk-client-row-${client.client_id}`}
                      valueClassName="text-rose-700"
                      valueHelper={client.credit_limit > 0 ? `${client.credit_used_pct.toFixed(0)}% do limite` : 'Sem limite definido'}
                      rowClassName={client.should_block ? 'border-rose-200 bg-rose-50/70' : ''}
                      badges={[
                        <Badge key="risk" className={cn('text-xs rounded-full', riskLevelColors[client.risk_level])}>{riskLevelLabels[client.risk_level]}</Badge>,
                        client.credit_limit > 0 ? <Badge key="limit" variant="outline" className="text-xs rounded-full border-slate-200 bg-slate-50">Limite: {formatCurrency(client.credit_limit)}</Badge> : null,
                      ].filter(Boolean)}
                      action={
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className={COMPACT_ACTION_BUTTONS.tab}
                          onClick={() => {
                            setSelectedClient(client);
                            setCreditLimit(client.credit_limit?.toString() || '');
                            setCreditLimitDialogOpen(true);
                          }}
                          data-testid={`risk-client-credit-limit-button-${client.client_id}`}
                        >
                          Definir Limite
                        </Button>
                      }
                    />
                  ))}
                </div>
              )}
          </RiskSectionCard>
        </TabsContent>

        {/* Fraud Tab */}
        <TabsContent value="fraud" className="mt-0 space-y-4">
          {/* Suspicious Activities */}
          <RiskSectionCard
            title="Actividades Suspeitas"
            description="Leitura operacional para priorizar investigação de fraude e desvios internos."
            icon={Siren}
            accent={(suspiciousActivities?.by_severity?.critical || 0) > 0 ? 'rose' : 'orange'}
            badge="Fraude"
            testId="risk-fraud-card"
            action={(
              <div className="flex flex-wrap gap-2">
                <Badge className="rounded-full bg-red-100 text-red-700">Críticas: {suspiciousActivities?.by_severity?.critical || 0}</Badge>
                <Badge className="rounded-full bg-orange-100 text-orange-700">Altas: {suspiciousActivities?.by_severity?.high || 0}</Badge>
                <Badge className="rounded-full bg-amber-100 text-amber-700">Médias: {suspiciousActivities?.by_severity?.medium || 0}</Badge>
              </div>
            )}
          >
              <div className="grid gap-4 md:grid-cols-3">
                <RiskMetricTile label="Alertas críticos" value={suspiciousActivities?.by_severity?.critical || 0} helper="Casos com maior urgência" icon={ShieldAlert} accent="rose" testId="risk-fraud-critical-metric" valueClassName="text-rose-700" />
                <RiskMetricTile label="Alertas altos" value={suspiciousActivities?.by_severity?.high || 0} helper="Monitoria activa necessária" icon={AlertTriangle} accent="orange" testId="risk-fraud-high-metric" valueClassName="text-orange-700" />
                <RiskMetricTile label="Alertas médios" value={suspiciousActivities?.by_severity?.medium || 0} helper="Sinais para revisão contextual" icon={SearchCheck} accent="amber" testId="risk-fraud-medium-metric" valueClassName="text-amber-700" />
              </div>

              {(suspiciousActivities?.activities || []).length === 0 ? (
                <div className="text-center py-8">
                  <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-2" />
                  <p className="text-muted-foreground">Nenhuma actividade suspeita detectada</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {(suspiciousActivities?.activities || []).map((activity, idx) => (
                    <RiskListRow
                      key={idx}
                      title={activity.type}
                      subtitle={activity.type === 'high_discount'
                        ? `Venda ${formatDisplayCode(activity.sale_number, 'SAL')}: desconto ${activity.discount_percentage}% (${formatCurrency(activity.discount_amount)})`
                        : activity.type === 'cashbox_discrepancy'
                          ? `Caixa ${activity.location}: diferença ${formatCurrency(activity.difference)} · Esperado ${formatCurrency(activity.expected)} · Real ${formatCurrency(activity.actual)}`
                          : activity.type === 'void_sale'
                            ? `Venda anulada ${formatDisplayCode(activity.sale_number, 'SAL')} (${formatCurrency(activity.total)}) · Motivo: ${activity.reason}`
                            : 'Sem detalhe adicional'}
                      value={activity.severity}
                      icon={activity.severity === 'critical' ? ShieldAlert : activity.severity === 'high' ? AlertTriangle : SearchCheck}
                      accent={activity.severity === 'critical' ? 'rose' : activity.severity === 'high' ? 'orange' : 'amber'}
                      testId={`risk-fraud-row-${idx}`}
                      valueClassName={activity.severity === 'critical' ? 'text-rose-700' : activity.severity === 'high' ? 'text-orange-700' : 'text-amber-700'}
                      valueHelper={`Por: ${activity.employee || activity.voided_by || 'Sistema'}`}
                      rowClassName={activity.severity === 'critical' ? 'border-rose-200 bg-rose-50/70' : activity.severity === 'high' ? 'border-orange-200 bg-orange-50/70' : 'border-amber-200 bg-amber-50/70'}
                      badges={[
                        <Badge key="type" className={cn('text-xs rounded-full', activity.severity === 'critical' ? 'bg-red-100 text-red-700' : activity.severity === 'high' ? 'bg-orange-100 text-orange-700' : 'bg-amber-100 text-amber-700')}>{activity.type}</Badge>,
                        <Badge key="severity" variant="outline" className="text-xs rounded-full border-slate-200 bg-white">{activity.severity}</Badge>,
                      ]}
                      action={<Button size="sm" variant="outline" className={COMPACT_ACTION_BUTTONS.tab}><Eye className="mr-1 h-3.5 w-3.5" />Investigar</Button>}
                    />
                  ))}
                </div>
              )}
          </RiskSectionCard>
        </TabsContent>

        {/* Locations Tab */}
        <TabsContent value="locations" className="mt-0 space-y-4" data-testid="risk-locations-tab-content">
          <RiskSectionCard
            title="Performance por Loja"
            description="Relatório operacional premium com leitura rápida por unidade, focado em pressão financeira, alertas e caixa."
            icon={Building2}
            accent="blue"
            badge="Lojas"
            testId="risk-locations-card"
            action={(
              <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => downloadSpreadsheetReport(
                      'locations-xlsx',
                      () => riskAPI.exportLocationPerformanceXlsx({ period: filters.period }),
                      `desempenho_lojas_${new Date().toISOString().slice(0, 10)}.xlsx`
                    )}
                    disabled={exportingReport === 'locations-xlsx' || !locationPerformance}
                    data-testid="risk-performance-xlsx-button"
                  >
                    <Download className="mr-2 h-4 w-4" />
                    {exportingReport === 'locations-xlsx' ? 'A gerar Excel...' : 'Exportar Excel'}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => downloadPdfReport(
                      'locations',
                      () => riskAPI.exportLocationPerformancePdf({ period: filters.period }),
                      `desempenho_lojas_${new Date().toISOString().slice(0, 10)}.pdf`
                    )}
                    disabled={exportingReport === 'locations' || !locationPerformance}
                    data-testid="risk-performance-pdf-button"
                  >
                    <Download className="mr-2 h-4 w-4" />
                    {exportingReport === 'locations' ? 'A gerar PDF...' : 'PDF Premium'}
                  </Button>
              </div>
            )}
          >
              {(locationPerformance?.warnings?.draining_locations || []).length > 0 && (
                <div className="flex flex-wrap items-center gap-2 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700" data-testid="risk-locations-warning-banner">
                  <AlertTriangle className="h-4 w-4" />
                  Lojas a drenar caixa: {locationPerformance.warnings.draining_locations.join(', ')}
                </div>
              )}

              <div className="grid gap-3 xl:grid-cols-12" data-testid="risk-locations-summary-cards">
                <div className={cn(DASHBOARD_SURFACE_CLASSNAME, 'p-5 xl:col-span-6')}>
                  <div className="flex items-center gap-3">
                    <div className="rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 p-3.5 shadow-lg text-white"><DollarSign className="h-5 w-5" /></div>
                    <div>
                      <p className="text-sm font-medium text-slate-500">receita</p>
                      <p className="mt-1 text-3xl font-semibold text-slate-950">{formatCurrency(locationPerformance?.summary?.total_revenue || 0)}</p>
                    </div>
                  </div>
                </div>
                <div className={cn(DASHBOARD_SURFACE_CLASSNAME, 'p-5 xl:col-span-6')}>
                  <div className="flex items-center gap-3">
                    <div className="rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 p-3.5 shadow-lg text-white"><TrendingUp className="h-5 w-5" /></div>
                    <div>
                      <p className="text-sm font-medium text-slate-500">resultado líquido</p>
                      <p className="mt-1 text-3xl font-semibold text-emerald-600">{formatCurrency(locationPerformance?.summary?.total_profit || 0)}</p>
                    </div>
                  </div>
                </div>
                <div className={cn(DASHBOARD_SURFACE_CLASSNAME, 'p-4 xl:col-span-3')}>
                  <p className="text-sm font-medium text-slate-500">despesas</p>
                  <p className="mt-2 text-2xl font-semibold text-slate-900">{formatCurrency(locationPerformance?.summary?.total_expenses || 0)}</p>
                </div>
                <div className={cn(DASHBOARD_SURFACE_CLASSNAME, 'p-4 xl:col-span-3')}>
                  <p className="text-sm font-medium text-slate-500">alertas</p>
                  <p className="mt-2 text-2xl font-semibold text-amber-600">{locationPerformance?.summary?.total_active_alerts || 0}</p>
                </div>
                <div className={cn(DASHBOARD_SURFACE_CLASSNAME, 'p-4 xl:col-span-3')}>
                  <p className="text-sm font-medium text-slate-500">inventário</p>
                  <p className="mt-2 text-2xl font-semibold text-violet-600">{locationPerformance?.summary?.total_inventory_items || 0}</p>
                </div>
                <div className={cn(DASHBOARD_SURFACE_CLASSNAME, 'p-4 xl:col-span-3')}>
                  <p className="text-sm font-medium text-slate-500">lojas em risco</p>
                  <p className="mt-2 text-2xl font-semibold text-rose-600">{(locationPerformance?.warnings?.draining_locations || []).length}</p>
                </div>
              </div>

              {(locationPerformance?.locations || []).length === 0 ? (
                <p className="py-8 text-center text-muted-foreground">Nenhuma loja encontrada</p>
              ) : (
                <div className="space-y-3" data-testid="risk-locations-report-list">
                  {(locationPerformance?.locations || []).map((loc) => (
                    <div
                      key={loc.location_id}
                      className={cn(
                        'rounded-2xl border p-3 transition-colors lg:p-4',
                        loc.status?.is_draining_cash && 'border-red-300 bg-red-50/75',
                        loc.status?.has_cashbox_issues && !loc.status?.is_draining_cash && 'border-amber-300 bg-amber-50/70',
                        !loc.status?.is_draining_cash && !loc.status?.has_cashbox_issues && DASHBOARD_SURFACE_CLASSNAME
                      )}
                      data-testid={`risk-location-card-${loc.location_id}`}
                    >
                      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                        <div className="space-y-3">
                          <div className="flex flex-wrap items-center gap-2">
                            {(() => {
                              const typeMeta = LOCATION_TYPE_META[loc.location_type] || LOCATION_TYPE_META.default;
                              const LocationIcon = typeMeta.icon;
                              return (
                                <>
                                  <div className={cn('rounded-2xl p-2', typeMeta.iconWrap)}>
                                    <LocationIcon className="h-4 w-4" />
                                  </div>
                                  <Badge variant="outline" className={cn('rounded-full border', typeMeta.badgeClass)}>
                                    {typeMeta.label}
                                  </Badge>
                                </>
                              );
                            })()}
                            <p className="text-lg font-semibold text-slate-900">{loc.location_name}</p>
                            <Badge className={loc.status?.is_profitable ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>
                              {loc.status?.is_profitable ? 'Lucrativa' : 'Deficitária'}
                            </Badge>
                            {loc.status?.has_cashbox_issues && <Badge className="bg-amber-100 text-amber-700">caixa</Badge>}
                            {loc.status?.has_alerts && <Badge className="bg-orange-100 text-orange-700">alertas</Badge>}
                          </div>
                          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
                            <RiskMetricTile label="vendas" value={formatCurrency(loc.metrics?.sales?.revenue || 0)} icon={DollarSign} accent="blue" />
                            <RiskMetricTile label="serviços" value={formatCurrency(loc.metrics?.services?.revenue || 0)} icon={Wrench} accent="emerald" />
                            <RiskMetricTile label="despesas" value={formatCurrency(loc.financials?.expenses || 0)} icon={Wallet} accent="rose" valueClassName="text-rose-700" />
                            <RiskMetricTile label="alertas" value={loc.metrics?.alerts?.active || 0} icon={Siren} accent="orange" valueClassName="text-orange-700" />
                            <RiskMetricTile label="inventário" value={loc.metrics?.inventory?.items || 0} icon={Package} accent="purple" valueClassName="text-violet-700" />
                          </div>
                        </div>
                        <div className="flex flex-col items-start gap-2 rounded-2xl border border-slate-200/80 bg-white/80 p-3 lg:min-w-[220px] lg:items-end">
                          <div className="text-left lg:text-right">
                            <p className="text-sm font-medium text-slate-500">resultado líquido</p>
                            <p className={cn('text-2xl font-semibold', (loc.financials?.net_profit || 0) >= 0 ? 'text-emerald-600' : 'text-red-600')}>
                              {formatCurrency(loc.financials?.net_profit || 0)}
                            </p>
                            <p className="text-xs text-muted-foreground">margem: {(loc.financials?.margin_pct || 0).toFixed(1)}%</p>
                          </div>
                          <div className="grid w-full gap-2 text-sm">
                            <div className="flex justify-between gap-3">
                              <span className="text-muted-foreground">dif. caixa</span>
                              <span className={cn('font-medium', (loc.cashbox?.total_discrepancy || 0) >= 0 ? 'text-emerald-600' : 'text-red-600')}>{formatCurrency(loc.cashbox?.total_discrepancy || 0)}</span>
                            </div>
                            <div className="flex justify-between gap-3">
                              <span className="text-muted-foreground">sessões</span>
                              <span className="font-medium text-slate-900">{loc.cashbox?.sessions || 0}</span>
                            </div>
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            className={`${COMPACT_ACTION_BUTTONS.tab} w-full lg:w-auto`}
                            onClick={() => handleViewLocationDetails(loc.location_id)}
                            disabled={loadingLocationDetailId === loc.location_id}
                            data-testid={`risk-location-details-button-${loc.location_id}`}
                          >
                            <Eye className="mr-2 h-3.5 w-3.5" />
                            {loadingLocationDetailId === loc.location_id ? 'A abrir...' : 'Ver detalhes'}
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
          </RiskSectionCard>
        </TabsContent>

        {/* Audit Tab */}
        <TabsContent value="audit" className="mt-0 space-y-4">
          <RiskSectionCard
            title="Log de Auditoria"
            description="Operações sensíveis registadas para rastreabilidade, revisão e conformidade operacional."
            icon={ClipboardList}
            accent="slate"
            badge="Auditoria"
            testId="risk-audit-card"
            action={(
              <Button
                  variant="outline"
                  size="sm"
                  onClick={() => downloadPdfReport(
                    'audit',
                    () => riskAPI.exportAuditOperationsPdf({ period: filters.period }),
                    `operacoes_sensiveis_${new Date().toISOString().slice(0, 10)}.pdf`
                  )}
                  disabled={exportingReport === 'audit' || auditLogs.length === 0}
                  data-testid="risk-audit-operations-pdf-button"
                >
                  <Download className="h-4 w-4 mr-2" />
                  {exportingReport === 'audit' ? 'A gerar PDF...' : 'PDF Premium'}
              </Button>
            )}
          >
              <div className="grid gap-4 md:grid-cols-3">
                <RiskMetricTile label="Operações registadas" value={auditLogs.length} helper="Eventos disponíveis nesta janela" icon={History} accent="slate" testId="risk-audit-total-metric" />
                <RiskMetricTile label="Último export" value={exportingReport === 'audit' ? 'Em curso' : 'Pronto'} helper="Estado do relatório premium" icon={Download} accent="blue" testId="risk-audit-export-metric" />
                <RiskMetricTile label="Conformidade" value={auditLogs.length > 0 ? 'Activo' : 'Sem eventos'} helper="Visibilidade das operações sensíveis" icon={Scale} accent="emerald" testId="risk-audit-status-metric" />
              </div>

              {auditLogs.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">Nenhuma operação registada</p>
              ) : (
                <div className="space-y-3 max-h-96 overflow-y-auto pr-1">
                  {auditLogs.slice(0, 50).map((log, idx) => (
                    <RiskListRow
                      key={log.id || idx}
                      title={log.action}
                      subtitle={`${log.entity_type} · Por: ${log.performed_by}`}
                      value={log.performed_at ? new Date(log.performed_at).toLocaleString('pt-MZ', { timeZone: 'Africa/Maputo' }) : '—'}
                      icon={FileText}
                      accent="slate"
                      testId={`risk-audit-row-${log.id || idx}`}
                    />
                  ))}
                </div>
              )}
          </RiskSectionCard>
        </TabsContent>
      </Tabs>

      <Dialog open={locationDetailDialogOpen} onOpenChange={setLocationDetailDialogOpen}>
        <DialogContent className="w-[min(92vw,1100px)] max-w-[1100px] max-h-[88vh] overflow-y-auto" data-testid="risk-location-detail-dialog">
          <DialogHeader>
            <DialogTitle>{selectedLocationHealth?.location?.name || 'Detalhes da Loja'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid gap-3 md:grid-cols-3" data-testid="risk-location-detail-summary">
              <div className="rounded-2xl border border-slate-200 bg-slate-50/90 p-4">
                <p className="text-[11px] uppercase tracking-[0.18em] text-slate-500">Tendência</p>
                <p className="mt-2 text-lg font-semibold text-slate-900">{selectedLocationHealth?.health?.revenue_trend || 'stable'}</p>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50/90 p-4">
                <p className="text-[11px] uppercase tracking-[0.18em] text-slate-500">Média diária</p>
                <p className="mt-2 text-lg font-semibold text-slate-900">{formatCurrency(selectedLocationHealth?.health?.daily_average || 0)}</p>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50/90 p-4">
                <p className="text-[11px] uppercase tracking-[0.18em] text-slate-500">Alertas activos</p>
                <p className="mt-2 text-lg font-semibold text-red-600">{selectedLocationHealth?.health?.active_alerts || 0}</p>
              </div>
            </div>

            <Card className={RISK_PANEL}>
              <CardHeader>
                <CardTitle className="text-base">Receita diária recente</CardTitle>
              </CardHeader>
              <CardContent>
                {Object.entries(selectedLocationHealth?.daily_revenue || {}).length === 0 ? (
                  <p className="text-sm text-muted-foreground">Sem dados recentes para esta loja.</p>
                ) : (
                  <div className="space-y-2" data-testid="risk-location-daily-revenue-list">
                    {Object.entries(selectedLocationHealth?.daily_revenue || {}).slice(-14).map(([day, amount]) => (
                      <div key={day} className="flex items-center justify-between rounded-xl border border-slate-200 px-3 py-2 text-sm">
                        <span>{day}</span>
                        <span className="font-medium text-slate-900">{formatCurrency(amount || 0)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className={RISK_PANEL}>
              <CardHeader>
                <CardTitle className="text-base">Alertas activos da loja</CardTitle>
              </CardHeader>
              <CardContent>
                {(selectedLocationHealth?.alerts || []).length === 0 ? (
                  <p className="text-sm text-muted-foreground">Sem alertas activos neste momento.</p>
                ) : (
                  <div className="space-y-2" data-testid="risk-location-alerts-list">
                    {(selectedLocationHealth?.alerts || []).map((alert) => (
                      <div key={alert.id} className="rounded-2xl border border-amber-200 bg-amber-50/80 p-3">
                        <div className="flex items-center justify-between gap-3">
                          <p className="font-medium text-slate-900">{alert.title || alert.rule_name || 'Alerta activo'}</p>
                          <Badge className="bg-amber-100 text-amber-700">{alert.severity || 'active'}</Badge>
                        </div>
                        <p className="mt-1 text-sm text-slate-600">{alert.description || alert.message || 'Sem descrição adicional.'}</p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </DialogContent>
      </Dialog>

      {/* Credit Limit Dialog */}
      <Dialog open={creditLimitDialogOpen} onOpenChange={setCreditLimitDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Definir Limite de Crédito</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Cliente</Label>
              <p className="font-medium">{selectedClient?.client_name}</p>
              <p className="text-sm text-muted-foreground">
                Dívida actual: {formatCurrency(selectedClient?.current_debt || 0)}
              </p>
            </div>
            <div className="space-y-2">
              <Label>Limite de Crédito (MZN)</Label>
              <Input 
                type="number"
                value={creditLimit}
                onChange={(e) => setCreditLimit(e.target.value)}
                placeholder="0.00"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreditLimitDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSetCreditLimit}>
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
